import { useConvex, useQuery } from "convex/react";
import { Settings, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StorageService } from "@/services/storageService";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useEditorStore } from "@/store/editorStore";
import { updateProject as updateLocalProject } from "@/data/adapters/local/projects";
import {
  restoreSnapshot as restoreLocalSnapshot,
  useSnapshotsByProject,
} from "@/data/adapters/local/projectSnapshots";
import type { Tables } from "@/types/database";
import { deleteProjectCompletely } from "@/utils/projectStorageCleanup";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { getCraftspaceFavicon } from "@/utils/craftspaceFaviconUtils";
import { api } from "../../convex/_generated/api";

type Project = Tables<"projects">;
type Panorama = Tables<"panoramas">;
type Hotspot = Tables<"hotspots">;

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

const formatDate = (value?: string) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const _ProjectSettings = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const convex = useConvex();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get state from editor store
  const {
    project: storeProject,
    panoramas: storePanoramas,
    hotspots: storeHotspots,
    loading: storeLoading,
    setProject: setStoreProject,
    setPanoramas: setStorePanoramas,
    setHotspots: setStoreHotspots,
    setLoading: setStoreLoading,
  } = useEditorStore();

  const [project, setProject] = useState<Project | null>(null);
  const [panoramas, setPanoramas] = useState<Panorama[]>([]);
  const [_hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteProjectName, setDeleteProjectName] = useState("");

  // Form state
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [socialImageUrl, setSocialImageUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [craftspaceFavicon, setCraftspaceFavicon] = useState<string | null>(null);
  const [socialImageUploading, setSocialImageUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const autosaveReadyRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string>("");
  const socialImageInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const [customFont, setCustomFont] = useState<File | null>(null);
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;

    try {
      setStoreLoading(true);
      // Fetch project details
      const projectData = await convex.query(api.projects.get, { id: projectId });

      if (!projectData) {
        throw new Error("Project not found");
      }

      setProject(projectData as Project);
      setStoreProject(projectData as Project);

      // Set form values
      setProjectName(projectData.name);
      setProjectDescription(projectData.description || "");

      // Parse project data for additional settings
      const data = projectData.data as Record<string, unknown>;
      setSocialImageUrl(data?.socialImageUrl || "");
      setFaviconUrl(data?.faviconUrl || "");

      // Fetch craftspace favicon for inheritance
      if (projectData.craftspace_id) {
        try {
          const craftspaceFaviconUrl = await getCraftspaceFavicon(projectData.craftspace_id);
          setCraftspaceFavicon(craftspaceFaviconUrl);
        } catch (error) {
          console.warn("Failed to fetch craftspace favicon:", error);
        }
      }

      // Fetch panoramas
      const panoramasData = await convex.query(api.panoramas.listByProject, { projectId });
      setPanoramas((panoramasData || []) as Panorama[]);
      setStorePanoramas((panoramasData || []) as Panorama[]);

      // Fetch hotspots
      const hotspotsData = await convex.query(api.hotspots.listByProject, { projectId });
      setHotspots((hotspotsData || []) as Hotspot[]);
      setStoreHotspots((hotspotsData || []) as Hotspot[]);
    } catch (error) {
      console.error("Error fetching project data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load project data",
      });
    } finally {
      setLoading(false);
      setStoreLoading(false);
    }
  }, [
    convex,
    projectId,
    setStoreHotspots,
    setStoreLoading,
    setStorePanoramas,
    setStoreProject,
    toast,
  ]);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [fetchProjectData, projectId]);

  // Redirect if not authenticated (Electron local mode bypasses auth)
  if (!user && !isElectron()) {
    return <Navigate to="/login" replace />;
  }

  const buildSnapshot = () => {
    const fontKey = customFont
      ? `${customFont.name}:${customFont.size}:${customFont.lastModified}`
      : "none";
    return JSON.stringify({
      projectName,
      projectDescription,
      socialImageUrl,
      faviconUrl,
      customFont: fontKey,
    });
  };

  const handleSave = async (options?: { silent?: boolean }) => {
    if (!project) return;

    setSaving(true);
    try {
      const currentData = (project.data as Record<string, unknown>) || {};
      const updatedData = {
        ...currentData,
        socialImageUrl,
        faviconUrl,
        customFontUrl: customFont ? await uploadCustomFont() : currentData.customFontUrl,
        // Track favicon source
        faviconSource: faviconUrl ? "custom" : "craftspace",
        // Keep craftspace favicon reference for inheritance
        inheritedCraftspaceFavicon: craftspaceFavicon,
      };

      await convex.mutation(api.projects.update, {
        id: project.id,
        name: projectName,
        description: projectDescription,
        data: updatedData,
      });

      setProject({
        ...project,
        name: projectName,
        description: projectDescription,
        data: updatedData,
      });

      if (!options?.silent) {
        toast({
          title: "Settings saved",
          description: "Your project settings have been updated.",
        });
      }

      setCustomFont(null);
      lastSavedSnapshotRef.current = buildSnapshot();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadCustomFont = async (): Promise<string | null> => {
    if (!customFont || !user || !project) return null;

    const craftspaceId = project.craftspace_id || "default";
    const timestamp = Date.now();
    const filename = `${timestamp}_${customFont.name}`;

    const privatePath = StoragePaths.private.skin(
      storageSegments.craftspaceKey,
      storageSegments.projectKey,
      filename,
    );

    const { data: _uploadData, error: uploadError } = await StorageService.uploadPrivate(
      "staging",
      privatePath,
      customFont,
    );

    if (uploadError) return null;

    const {
      data: { signedUrl },
    } = await StorageService.getPrivateUrl("staging", privatePath);

    return signedUrl;
  };

  const handleDeleteProject = async () => {
    if (!project || deleteProjectName !== project.name) return;

    try {
      // Use comprehensive deletion with storage cleanup
      const result = await deleteProjectCompletely(
        project.id,
        project.craftspace_id || "default",
        storageSegments,
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to delete project");
      }

      toast({
        title: "Project deleted",
        description: `Your project has been permanently deleted. ${result.deletedFiles} files removed from storage.`,
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete project",
      });
    }
  };

  if (loading || storeLoading) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="text-white">Loading project settings...</div>
      </div>
    );
  }

  if (!project || !storeProject) {
    return <Navigate to="/dashboard" replace />;
  }

  const acceptedFontTypes = [".ttf", ".otf", ".eot", ".woff", ".woff2"];

  return (
    <div className="bg-gray-800 flex flex-col">
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto py-4 px-4 pt-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Settings size={32} className="text-white" />
                Project Settings
              </h1>
              <p className="text-gray-300">{project.name}</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* General Settings */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">General</CardTitle>
                <CardDescription className="text-gray-400">
                  Basic project information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="projectName" className="text-gray-300">
                    Project Name
                  </Label>
                  <Input
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label htmlFor="projectDescription" className="text-gray-300">
                    Description
                  </Label>
                  <Textarea
                    id="projectDescription"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    placeholder="Enter project description"
                    rows={3}
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label htmlFor="craftspaceId" className="text-gray-300">
                    Craftspace ID
                  </Label>
                  <Input
                    id="craftspaceId"
                    value={project.craftspace_id || "default"}
                    readOnly
                    className="bg-gray-700 border-gray-600 text-gray-300 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    This project belongs to the specified craftspace
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social & Branding */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Social & Branding</CardTitle>
                <CardDescription className="text-gray-400">
                  Configure how your project appears when shared
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Social Media Image */}
                <div>
                  <Label htmlFor="socialImage" className="text-gray-300">
                    Social Media Image
                  </Label>
                  {(() => {
                    // Get thumbnail from first panorama (only if processing is completed)
                    const firstPanorama = panoramas?.[0];
                    const projectThumbnail =
                      firstPanorama?.thumbnail_url &&
                      firstPanorama?.processing_status === "completed"
                        ? firstPanorama.thumbnail_url
                        : null;

                    const previewUrl = socialImageUrl || projectThumbnail || "/placeholder.svg";
                    return (
                      <div className="mb-2">
                        <img
                          src={previewUrl}
                          alt="Social Preview"
                          className="rounded w-32 h-32 object-cover border border-gray-600 bg-gray-800"
                        />
                      </div>
                    );
                  })()}
                  <input
                    type="file"
                    accept="image/*"
                    ref={socialImageInputRef}
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !project) return;
                      setSocialImageUploading(true);
                      const craftspaceId = project.craftspace_id || "default";
                      const timestamp = Date.now();
                      const filename = `${timestamp}_social_${file.name}`;
                      const privatePath = StoragePaths.private.skin(
                        storageSegments.craftspaceKey,
                        storageSegments.projectKey,
                        filename,
                      );
                      const { error } = await StorageService.uploadPrivate(
                        "staging",
                        privatePath,
                        file,
                      );
                      if (!error) {
                        const {
                          data: { signedUrl },
                        } = await StorageService.getPrivateUrl("staging", privatePath);
                        setSocialImageUrl(signedUrl);
                      } else {
                        toast({
                          variant: "destructive",
                          title: "Upload failed",
                          description: "Failed to upload social image.",
                        });
                      }
                      setSocialImageUploading(false);
                    }}
                  />
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
                      onClick={() => socialImageInputRef.current?.click()}
                      disabled={socialImageUploading}
                    >
                      {socialImageUploading
                        ? "Uploading..."
                        : socialImageUrl
                          ? "Replace Image"
                          : "Upload Image"}
                    </Button>
                    {socialImageUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
                        onClick={() => setSocialImageUrl("")}
                        disabled={socialImageUploading}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                {/* Favicon */}
                <div>
                  {(faviconUrl || craftspaceFavicon) && (
                    <div className="mb-2">
                      <img
                        src={faviconUrl || craftspaceFavicon}
                        alt="Favicon Preview"
                        className="rounded w-12 h-12 object-cover border border-gray-600"
                      />
                      {!faviconUrl && craftspaceFavicon && (
                        <p className="text-xs text-gray-400 mt-1">Using craftspace favicon</p>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={faviconInputRef}
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !project) return;
                      setFaviconUploading(true);
                      const craftspaceId = project.craftspace_id || "default";
                      const timestamp = Date.now();
                      const filename = `${timestamp}_favicon_${file.name}`;
                      const privatePath = StoragePaths.private.skin(
                        storageSegments.craftspaceKey,
                        storageSegments.projectKey,
                        filename,
                      );
                      const { error } = await StorageService.uploadPrivate(
                        "staging",
                        privatePath,
                        file,
                      );
                      if (!error) {
                        const {
                          data: { signedUrl },
                        } = await StorageService.getPrivateUrl("staging", privatePath);
                        setFaviconUrl(signedUrl);
                      } else {
                        toast({
                          variant: "destructive",
                          title: "Upload failed",
                          description: "Failed to upload favicon.",
                        });
                      }
                      setFaviconUploading(false);
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-1 border border-white/20 text-white hover:bg-white/10 hover:text-white"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={faviconUploading}
                  >
                    {faviconUploading
                      ? "Uploading..."
                      : faviconUrl
                        ? "Replace Favicon"
                        : craftspaceFavicon
                          ? "Upload Custom Favicon"
                          : "Upload Favicon"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Custom Font */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Custom Font</CardTitle>
                <CardDescription className="text-gray-400">
                  Upload a custom font for your virtual tour
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label htmlFor="font" className="text-gray-300">
                    Font File ({acceptedFontTypes.join(", ")})
                  </Label>
                  <Input
                    id="font"
                    type="file"
                    accept={acceptedFontTypes.join(",")}
                    onChange={(e) => setCustomFont(e.target.files?.[0] || null)}
                    className="bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:border-gray-500 file:text-white"
                  />
                  {customFont && (
                    <p className="text-sm text-gray-400 mt-1">Selected: {customFont.name}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Overview Statistics</CardTitle>
                <CardDescription className="text-gray-400">
                  Project information and statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-white">Project ID</p>
                    <p className="text-gray-400 font-mono text-xs">{project.id}</p>
                  </div>
                  <div>
                    <p className="font-medium text-white">Created</p>
                    <p className="text-gray-400">
                      {project.created_at ? new Date(project.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-white">Last Updated</p>
                    <p className="text-gray-400">
                      {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-white">Panoramas</p>
                    <p className="text-gray-400">{storePanoramas.length}</p>
                  </div>
                  <div>
                    <p className="font-medium text-white">Hotspots</p>
                    <p className="text-gray-400">{storeHotspots.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-between">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Project
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-gray-800 border-gray-700">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Delete Project</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This action cannot be undone. Please type the project name "{project.name}" to
                      confirm deletion.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    value={deleteProjectName}
                    onChange={(e) => setDeleteProjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && deleteProjectName === project.name) {
                        handleDeleteProject();
                      }
                    }}
                    placeholder={`Type "${project.name}" to confirm`}
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteProject}
                      disabled={deleteProjectName !== project.name}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete Project
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SettingsPanel = () => {
  const convex = useConvex();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get state from editor store (already loaded by ProjectCraftspace)
  const {
    panoramas: storePanoramas,
    hotspots: storeHotspots,
    project,
    setProject,
  } = useEditorStore();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const useLocalBackend =
    isElectron() && (projectBackend === "local" || (project?.id && isUuid(project.id)));

  const localSnapshots = useSnapshotsByProject(useLocalBackend ? project?.id : undefined);
  const cloudSnapshots = useQuery(
    api.projectSnapshots.listByProject,
    !useLocalBackend && project ? { project_id: project.id } : "skip",
  );
  const snapshots = useLocalBackend ? localSnapshots : cloudSnapshots;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteProjectName, setDeleteProjectName] = useState("");
  const [restoreTarget, setRestoreTarget] = useState<any | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Form state
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [socialImageUrl, setSocialImageUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [craftspaceFavicon, setCraftspaceFavicon] = useState<string | null>(null);
  const [socialImageUploading, setSocialImageUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const autosaveReadyRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string>("");
  const socialImageInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const [customFont, _setCustomFont] = useState<File | null>(null);
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        storagePath:
          typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  const handleRestoreBackup = async () => {
    if (!project || !restoreTarget) return;
    setRestoring(true);
    try {
      if (useLocalBackend) {
        await restoreLocalSnapshot(restoreTarget._id || restoreTarget.id);
      } else {
        await convex.mutation(api.projectSnapshots.restore, {
          id: restoreTarget._id || restoreTarget.id,
        });
      }

      const snapshotData = restoreTarget.snapshot_data as any;
      if (snapshotData?.project) {
        const nextProject = {
          ...project,
          name: snapshotData.project.name ?? project.name,
          description:
            snapshotData.project.description !== undefined
              ? snapshotData.project.description
              : project.description,
          data:
            snapshotData.project.data !== undefined
              ? snapshotData.project.data
              : project.data,
        };
        setProject(nextProject);
        setProjectName(nextProject.name);
        setProjectDescription(nextProject.description || "");

        const data = (nextProject.data as Record<string, unknown>) || {};
        setSocialImageUrl(data?.socialImageUrl || "");
        setFaviconUrl(data?.faviconUrl || "");
      }

      toast({
        title: "Backup restored",
        description: restoreTarget.name ? `"${restoreTarget.name}" restored.` : "Backup restored.",
      });
      setRestoreTarget(null);
    } catch (error) {
      console.error("Restore failed:", error);
      toast({
        variant: "destructive",
        title: "Restore failed",
        description: "Could not restore this backup.",
      });
    } finally {
      setRestoring(false);
    }
  };

  // Initialize form data when project is available from store
  useEffect(() => {
    if (project) {
      // Set form values
      setProjectName(project.name);
      setProjectDescription(project.description || "");

      // Parse project data for additional settings
      const data = project.data as Record<string, unknown>;
      setSocialImageUrl(data?.socialImageUrl || "");
      setFaviconUrl(data?.faviconUrl || "");

      // Fetch craftspace favicon for inheritance
      if (project.craftspace_id) {
        getCraftspaceFavicon(project.craftspace_id)
          .then(setCraftspaceFavicon)
          .catch((error) => console.warn("Failed to fetch craftspace favicon:", error));
      }

      setLoading(false);
    }
  }, [project]);

  const buildSnapshot = () => {
    const fontKey = customFont
      ? `${customFont.name}:${customFont.size}:${customFont.lastModified}`
      : "none";
    return JSON.stringify({
      projectName,
      projectDescription,
      socialImageUrl,
      faviconUrl,
      customFont: fontKey,
    });
  };

  useEffect(() => {
    if (!project || loading) return;

    const snapshot = buildSnapshot();
    if (!autosaveReadyRef.current) {
      autosaveReadyRef.current = true;
      lastSavedSnapshotRef.current = snapshot;
      return;
    }

    if (snapshot === lastSavedSnapshotRef.current) return;

    const timeout = setTimeout(() => {
      if (saving) return;
      void handleSave({ silent: true });
    }, 700);

    return () => clearTimeout(timeout);
  }, [
    project,
    loading,
    projectName,
    projectDescription,
    socialImageUrl,
    faviconUrl,
    customFont,
    saving,
  ]);

  const handleSave = async (options?: { silent?: boolean }) => {
    if (!project) return;

    setSaving(true);
    try {
      const currentData = (project.data as Record<string, unknown>) || {};
      const updatedData = {
        ...currentData,
        socialImageUrl,
        faviconUrl,
        customFontUrl: customFont ? await uploadCustomFont() : currentData.customFontUrl,
        // Track favicon source
        faviconSource: faviconUrl ? "custom" : "craftspace",
        // Keep craftspace favicon reference for inheritance
        inheritedCraftspaceFavicon: craftspaceFavicon,
      };

      if (useLocalBackend) {
        await updateLocalProject({
          id: project.id,
          craftspace_id: project.craftspace_id || "default",
          name: projectName,
          description: projectDescription,
          data: updatedData,
        });
      } else {
        await convex.mutation(api.projects.update, {
          id: project.id,
          name: projectName,
          description: projectDescription,
          data: updatedData,
        });
      }

      setProject({
        ...project,
        name: projectName,
        description: projectDescription,
        data: updatedData,
      });

      if (!options?.silent) {
        toast({
          title: "Settings saved",
          description: "Your project settings have been updated.",
        });
      }

      _setCustomFont(null);
      lastSavedSnapshotRef.current = buildSnapshot();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const persistProjectData = async (nextData: Record<string, unknown>) => {
    if (!project) return;
    try {
      if (useLocalBackend) {
        await updateLocalProject({
          id: project.id,
          craftspace_id: project.craftspace_id || "default",
          data: nextData,
        });
      } else {
        await convex.mutation(api.projects.update, { id: project.id, data: nextData });
      }
      setProject({
        ...project,
        data: nextData,
      });
    } catch (error) {
      console.error("Error saving project data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save settings",
      });
    }
  };

  const uploadCustomFont = async (): Promise<string | null> => {
    if (!customFont || !project) return null;

    const timestamp = Date.now();
    const filename = `${timestamp}_${customFont.name}`;

    const privatePath = StoragePaths.private.skin(
      storageSegments.craftspaceKey,
      storageSegments.projectKey,
      filename,
    );

    try {
      const storageService = await StorageServiceFactory.createStorageService(
        project.craftspace_id || "default",
      );
      await storageService.uploadFile(customFont, privatePath);
      return await storageService.getFileUrl(privatePath);
    } catch (error) {
      console.error("Failed to upload custom font:", error);
      return null;
    }
  };

  const handleDeleteProject = async () => {
    if (!project || deleteProjectName !== project.name) return;

    try {
      // Use comprehensive deletion with storage cleanup
      const result = await deleteProjectCompletely(
        project.id,
        project.craftspace_id || "default",
        storageSegments,
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to delete project");
      }

      toast({
        title: "Project deleted",
        description: `Your project has been permanently deleted. ${result.deletedFiles} files removed from storage.`,
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete project",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-white">Loading settings...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-white">Project not found</div>
      </div>
    );
  }

  const _acceptedFontTypes = [".ttf", ".otf", ".eot", ".woff", ".woff2"];

  // Only render the settings content (right panel)
  return (
    <div className="flex-1 overflow-auto" style={{ marginTop: "57px" }}>
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Settings size={32} className="text-white" />
              Project Settings
            </h1>
            <p className="text-gray-300">{project.name}</p>
          </div>
        </div>

        <div className="space-y-8">
          {/* General Settings */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">General</CardTitle>
              <CardDescription className="text-gray-400">Basic project information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="projectName" className="text-gray-300">
                  Project Name
                </Label>
                <Input
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label htmlFor="projectDescription" className="text-gray-300">
                  Description
                </Label>
                <Textarea
                  id="projectDescription"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="Enter project description"
                  rows={2}
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>
              <div>
                <Label htmlFor="craftspaceId" className="text-gray-300">
                  Craftspace ID
                </Label>
                <Input
                  id="craftspaceId"
                  value={project.craftspace_id || "default"}
                  readOnly
                  className="bg-gray-700 border-gray-600 text-gray-300 font-mono text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  This project belongs to the specified craftspace
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Backups */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Backups</CardTitle>
              <CardDescription className="text-gray-400">
                Create a backup anytime with Cmd/Ctrl+Shift+S
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {snapshots && snapshots.length > 0 ? (
                snapshots.map((snapshot: any) => (
                  <div
                    key={snapshot._id || snapshot.id}
                    className="flex items-center justify-between gap-4 rounded border border-gray-700 bg-gray-700/40 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        {snapshot.name || "Untitled backup"}
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatDate(snapshot.created_at)} · {formatBytes(snapshot.size_bytes)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                        {snapshot.trigger || "manual"}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-gray-200 hover:text-white hover:bg-gray-700"
                        onClick={() => setRestoreTarget(snapshot)}
                      >
                        Restore
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400">No backups created yet.</div>
              )}

              <AlertDialog open={!!restoreTarget} onOpenChange={(open) => !open && setRestoreTarget(null)}>
                <AlertDialogContent className="bg-gray-800 border-gray-700">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Restore Backup</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This will restore project settings, panoramas metadata, and hotspots from the
                      selected backup. Asset files are unchanged.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRestoreBackup}
                      disabled={restoring}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {restoring ? "Restoring..." : "Restore Backup"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>

          {/* Social & Branding */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Social & Branding</CardTitle>
              <CardDescription className="text-gray-400">
                Configure how your project appears when shared
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Social Media Image */}
              <div>
                <Label htmlFor="socialImage" className="text-gray-300">
                  Social Media Image
                </Label>
                {(() => {
                  // Get thumbnail from first panorama (only if processing is completed)
                  const firstPanorama = storePanoramas?.[0];
                  const projectThumbnail =
                    firstPanorama?.thumbnail_url && firstPanorama?.processing_status === "completed"
                      ? firstPanorama.thumbnail_url
                      : null;

                  const previewUrl = socialImageUrl || projectThumbnail || "/placeholder.svg";
                  return (
                    <div className="mb-2">
                      <img
                        src={previewUrl}
                        alt="Social Preview"
                        className="rounded w-32 h-32 object-cover border border-gray-600 bg-gray-800"
                      />
                    </div>
                  );
                })()}
                <input
                  type="file"
                  accept="image/*"
                  ref={socialImageInputRef}
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !project) return;
                    setSocialImageUploading(true);
                    const projectData = project.data as Record<string, unknown> | null;
                    const resolvedSegments = resolveStoragePathSegments({
                      craftspaceId: project.craftspace_id || "default",
                      projectId: project.id,
                      projectName: project.name,
                      projectData,
                      userEmail: user?.email,
                      userId: user?.id,
                      storagePath:
                        typeof projectData?.storage_path === "string"
                          ? projectData.storage_path
                          : null,
                    });
                    const timestamp = Date.now();
                    const filename = `${timestamp}_social_${file.name}`;
                    const privatePath = StoragePaths.private.skin(
                      resolvedSegments.craftspaceKey,
                      resolvedSegments.projectKey,
                      filename,
                    );
                    try {
                      const storageService = await StorageServiceFactory.createStorageService(
                        project.craftspace_id || "default",
                      );
                      await storageService.uploadFile(file, privatePath);
                      const fileUrl = await storageService.getFileUrl(privatePath);
                      setSocialImageUrl(fileUrl);
                      const currentData = (project.data as Record<string, unknown>) || {};
                      await persistProjectData({
                        ...currentData,
                        socialImageUrl: fileUrl,
                      });
                    } catch (error) {
                      console.error("Failed to upload social image:", error);
                      toast({
                        variant: "destructive",
                        title: "Upload failed",
                        description: "Failed to upload social image.",
                      });
                    }
                    setSocialImageUploading(false);
                  }}
                />
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
                    onClick={() => socialImageInputRef.current?.click()}
                    disabled={socialImageUploading}
                  >
                    {socialImageUploading
                      ? "Uploading..."
                      : socialImageUrl
                        ? "Replace Image"
                        : "Upload Image"}
                  </Button>
                  {socialImageUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
                      onClick={async () => {
                        setSocialImageUrl("");
                        if (project) {
                          const currentData = (project.data as Record<string, unknown>) || {};
                          await persistProjectData({
                            ...currentData,
                            socialImageUrl: "",
                          });
                        }
                      }}
                      disabled={socialImageUploading}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              {/* Favicon */}
              <div>
                {(faviconUrl || craftspaceFavicon) && (
                  <div className="mb-2">
                    <img
                      src={faviconUrl || craftspaceFavicon}
                      alt="Favicon Preview"
                      className="rounded w-12 h-12 object-cover border border-gray-600"
                    />
                    {!faviconUrl && craftspaceFavicon && (
                      <p className="text-xs text-gray-400 mt-1">Using craftspace favicon</p>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={faviconInputRef}
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !project) return;
                    setFaviconUploading(true);
                    const projectData = project.data as Record<string, unknown> | null;
                    const resolvedSegments = resolveStoragePathSegments({
                      craftspaceId: project.craftspace_id || "default",
                      projectId: project.id,
                      projectName: project.name,
                      projectData,
                      userEmail: user?.email,
                      userId: user?.id,
                      storagePath:
                        typeof projectData?.storage_path === "string"
                          ? projectData.storage_path
                          : null,
                    });
                    const timestamp = Date.now();
                    const filename = `${timestamp}_favicon_${file.name}`;
                    const privatePath = StoragePaths.private.skin(
                      resolvedSegments.craftspaceKey,
                      resolvedSegments.projectKey,
                      filename,
                    );
                    try {
                      const storageService = await StorageServiceFactory.createStorageService(
                        project.craftspace_id || "default",
                      );
                      await storageService.uploadFile(file, privatePath);
                      const fileUrl = await storageService.getFileUrl(privatePath);
                      setFaviconUrl(fileUrl);
                      const currentData = (project.data as Record<string, unknown>) || {};
                      await persistProjectData({
                        ...currentData,
                        faviconUrl: fileUrl,
                        faviconSource: "custom",
                      });
                    } catch (error) {
                      console.error("Failed to upload favicon:", error);
                      toast({
                        variant: "destructive",
                        title: "Upload failed",
                        description: "Failed to upload favicon.",
                      });
                    }
                    setFaviconUploading(false);
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 border border-white/20 text-white hover:bg-white/10 hover:text-white"
                  onClick={() => faviconInputRef.current?.click()}
                  disabled={faviconUploading}
                >
                  {faviconUploading
                    ? "Uploading..."
                    : faviconUrl
                      ? "Replace Favicon"
                      : craftspaceFavicon
                        ? "Upload Custom Favicon"
                        : "Upload Favicon"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Overview Statistics</CardTitle>
              <CardDescription className="text-gray-400">
                Project information and statistics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium text-white">Panoramas</p>
                  <p className="text-gray-400">{storePanoramas.length}</p>
                </div>
                <div>
                  <p className="font-medium text-white">Hotspots</p>
                  <p className="text-gray-400">{storeHotspots.length}</p>
                </div>
                <div>
                  <p className="font-medium text-white">Created</p>
                  <p className="text-gray-400">
                    {project.created_at ? new Date(project.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-white">Updated</p>
                  <p className="text-gray-400">
                    {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-between">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Project
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-gray-800 border-gray-700">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Delete Project</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    This action cannot be undone. Please type the project name "{project.name}" to
                    confirm deletion.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Input
                  value={deleteProjectName}
                  onChange={(e) => setDeleteProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && deleteProjectName === project.name) {
                      handleDeleteProject();
                    }
                  }}
                  placeholder={`Type "${project.name}" to confirm`}
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteProject}
                    disabled={deleteProjectName !== project.name}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete Project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

          </div>
        </div>
      </div>
    </div>
  );
};
